// portal-socket.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { AuthenticationService } from './authentication.service';
import { environment } from 'src/environments/environment';
import {
  WebsiteModel,
  WebsiteProcessModel,
} from 'src/app/components/user-dashboard/portal/portal.model';
import {
  AccountSetupStep,
  MessengerThread,
} from 'src/app/models/owner-dashboard';
import { NotificationElement } from 'src/app/models/dashboard.shared';

export enum SocketConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  TERMINATED = 'terminated',
}

@Injectable({
  providedIn: 'root',
})
export class PortalSocketService implements OnDestroy {
  // Event streams using ReplaySubject to ensure subscribers don't miss initial events
  readonly websiteProcess$ = new ReplaySubject<WebsiteProcessModel>(1);
  readonly website$ = new ReplaySubject<WebsiteModel>(1);
  readonly messenger$ = new ReplaySubject<MessengerThread>(1);
  readonly notifications$ = new ReplaySubject<NotificationElement>(1);
  readonly accountSetup$ = new ReplaySubject<AccountSetupStep>(1);
  readonly socketReady$ = new ReplaySubject<void>(1);

  // Connection status observable
  readonly connectionStatus$ = new BehaviorSubject<SocketConnectionStatus>(
    SocketConnectionStatus.DISCONNECTED
  );

  private socket: Socket | null = null;
  private destroy$ = new Subject<void>();
  private reconnectInterval: ReturnType<typeof setInterval> | null = null;
  private isConnected = false;

  constructor(private authService: AuthenticationService) {
    this.initializeSocketConnection();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeSocket();
  }

  /**
   * Initialize socket connection based on auth token changes
   */
  private initializeSocketConnection(): void {
    this.authService.token
      .pipe(
        filter((token) => token !== null && token !== undefined),
        takeUntil(this.destroy$)
      )
      .subscribe((token) => {
        this.reconnectSocket(token);
      });

    this.authService.onLogout.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.closeSocket();
    });
  }

  /**
   * Reconnect socket with new token
   */
  private reconnectSocket(token: string): void {
    this.closeSocket();
    this.connectSocket(token);
  }

  /**
   * Establish socket connection
   */
  private connectSocket(token: string): void {
    if (this.isConnected) {
      return;
    }

    const config: SocketIoConfig = {
      url: environment.api.socketUrl,
      options: {
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 15000,
        withCredentials: true,
        path: environment.api.subPath,
        transports: ['websocket', 'polling'],
        query: { authorization: `bearer ${token}` },
      },
    };

    try {
      this.socket = new Socket(config);
      this.setupConnectionHandlers();
      this.setupEventListeners();
      this.isConnected = true;
      this.socketReady$.next();
    } catch (error) {
      console.error('Socket connection failed:', error);
      this.connectionStatus$.next(SocketConnectionStatus.DISCONNECTED);
    }
  }

  /**
   * Setup connection event handlers
   */
  private setupConnectionHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.handleConnect();
    });

    this.socket.on('reconnect', () => {
      this.handleReconnect();
    });

    this.socket.on('disconnect', () => {
      this.handleDisconnect();
    });

    this.socket.on('connect_error', (error: Error) => {
      this.handleConnectionError(error);
    });
  }

  /**
   * Setup business logic event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    const eventMappings = [
      { event: 'websiteProcess', subject: this.websiteProcess$ },
      { event: 'website', subject: this.website$ },
      { event: 'notification', subject: this.notifications$ },
      { event: 'message', subject: this.messenger$ },
      { event: 'setup', subject: this.accountSetup$ },
    ];

    eventMappings.forEach(({ event, subject }) => {
      this.socket
        ?.fromEvent(event)
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: any) => {
          subject.next(data);
        });
    });
  }

  /**
   * Handle successful connection
   */
  private handleConnect(): void {
    this.connectionStatus$.next(SocketConnectionStatus.CONNECTED);
    this.clearReconnectInterval();
  }

  /**
   * Handle reconnection
   */
  private handleReconnect(): void {
    this.connectionStatus$.next(SocketConnectionStatus.CONNECTED);
    this.clearReconnectInterval();
  }

  /**
   * Handle disconnection
   */
  private handleDisconnect(): void {
    if (this.connectionStatus$.value === SocketConnectionStatus.TERMINATED) {
      return;
    }

    this.connectionStatus$.next(SocketConnectionStatus.DISCONNECTED);
    this.startReconnectLoop();
  }

  /**
   * Handle connection errors
   */
  private handleConnectionError(error: Error): void {
    console.error('Socket connection error:', error);
    this.connectionStatus$.next(SocketConnectionStatus.RECONNECTING);
  }

  /**
   * Start reconnection loop
   */
  private startReconnectLoop(): void {
    if (this.reconnectInterval) {
      return;
    }

    this.connectionStatus$.next(SocketConnectionStatus.RECONNECTING);

    this.reconnectInterval = setInterval(() => {
      if (this.socket && !this.socket.ioSocket.connected) {
        this.socket.connect();
      }

      if (this.connectionStatus$.value === SocketConnectionStatus.CONNECTED) {
        this.clearReconnectInterval();
      }
    }, 2000);
  }

  /**
   * Clear reconnection interval
   */
  private clearReconnectInterval(): void {
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval);
      this.reconnectInterval = null;
    }
  }

  /**
   * Close socket connection and cleanup
   */
  private closeSocket(): void {
    if (!this.socket) {
      return;
    }

    this.clearReconnectInterval();
    this.connectionStatus$.next(SocketConnectionStatus.TERMINATED);

    this.socket.disconnect();
    this.socket.removeAllListeners();
    this.socket = null;
    this.isConnected = false;

    // Reset all subjects for clean reconnection
    this.resetSubjects();
  }

  /**
   * Reset all event subjects
   */
  private resetSubjects(): void {
    [
      this.websiteProcess$,
      this.website$,
      this.messenger$,
      this.notifications$,
      this.accountSetup$,
    ].forEach((subject) => {
      if (subject instanceof ReplaySubject) {
        // ReplaySubject doesn't have a reset, so we handle cleanup differently
        // New subscriptions will wait for new emissions
      }
    });
  }

  /**
   * Emit custom event to socket
   */
  emit(event: string, data?: any): void {
    if (
      this.socket &&
      this.connectionStatus$.value === SocketConnectionStatus.CONNECTED
    ) {
      this.socket.emit(event, data);
    } else {
      console.warn(`Cannot emit event "${event}": Socket not connected`);
    }
  }

  /**
   * Check if socket is connected
   */
  get isSocketConnected(): boolean {
    return this.connectionStatus$.value === SocketConnectionStatus.CONNECTED;
  }
}
